import { ComponentType as ArrayComponentType, BooleanComponentType, FieldType, ImageFit, InputType, MultilineAddressComponentType, NumberComponentType, NumberOfColumns, PaymentComponentType, StringComponentType, UploadFileFormat, ValidationFormat, WixFileComponentType } from '@wix/ambassador-forms-v4-form/types';
import { Breakpoint, DEFAULT_BREAKPOINT } from '../constants';
const GRID_DEFAULTS = {
  columns: 12,
  rowHeight: 24,
  margin: {
    vertical: 24,
    horizontal: 24
  },
  padding: {
    vertical: 0,
    horizontal: 0
  }
};
export function createFormView(_ref) {
  var _form$steps, _form$steps2, _layout$small;
  let {
    form,
    path = '',
    stepId,
    recursive = true,
    includeHiddenFields = false,
    isMobile = false,
    breakpoint = DEFAULT_BREAKPOINT,
    useFieldsV2 = false,
    parentId = ''
  } = _ref;
  const step = stepId ? (_form$steps = form.steps) == null ? void 0 : _form$steps.find(_ref2 => {
    let {
      id
    } = _ref2;
    return id === stepId;
  }) : (_form$steps2 = form.steps) == null ? void 0 : _form$steps2[0];
  const layout = (step == null ? void 0 : step.layout) ?? {};
  const fields = form.fields ?? [];
  const fieldsV2 = form.fieldsV2;
  let nameView = '';
  if ('properties' in form) {
    var _form$properties;
    nameView = ((_form$properties = form.properties) == null ? void 0 : _form$properties.name) ?? '';
  } else if ('name' in form) {
    nameView = form.name ?? '';
  }
  const hasSmallLayout = (_layout$small = layout.small) == null ? void 0 : _layout$small.items;
  const isSmallBreakpoint = isMobile && hasSmallLayout;
  const fieldViews = (useFieldsV2 && fieldsV2 ? fieldsV2 : fields).filter(field => includeHiddenFields || !field.hidden).filter(field => {
    var _layout$large;
    return layout == null || (_layout$large = layout.large) == null || (_layout$large = _layout$large.items) == null ? void 0 : _layout$large.find(item => field.id === item.fieldId);
  }).reduce((result, field) => {
    const fieldView = useFieldsV2 && fieldsV2 ? createFieldViewV2(field, layout, path, recursive, isMobile, isSmallBreakpoint ? Breakpoint.SMALL : breakpoint) : createFieldView(field, layout, path, recursive, isMobile, isSmallBreakpoint ? Breakpoint.SMALL : breakpoint);
    if (fieldView) {
      result.push(fieldView);
    }
    return result;
  }, []).sort(sortFieldViewsByColumn).sort(sortFieldViewsByRow);
  const gridView = createGridView(layout, isMobile);
  const formView = {
    parentId,
    name: nameView,
    grid: gridView,
    fields: fieldViews
  };
  if (isMobile && !hasSmallLayout || breakpoint === Breakpoint.SMALL && !hasSmallLayout) {
    return applyMobileLayout(formView);
  }
  return formView;
}
function applyMobileLayout(form) {
  const {
    fields
  } = form.fields.reduce((state, field) => {
    const layout = {
      width: form.grid.columns,
      height: field.layout.height,
      column: 0,
      row: state.row
    };
    state.fields.push({
      ...field,
      layout
    });
    state.row += field.layout.height;
    return state;
  }, {
    row: 0,
    fields: []
  });
  return {
    ...form,
    fields
  };
}
function createFieldView(field, layout, path, recursive, isMobile, breakpoint) {
  var _field$view;
  if (breakpoint === void 0) {
    breakpoint = DEFAULT_BREAKPOINT;
  }
  if (!(field.id && (_field$view = field.view) != null && _field$view.fieldType)) {
    return;
  }
  if (!layout) {
    return;
  }
  const breakpoints = createLayoutBreakpoints(layout, field.id);
  const activeLayout = breakpoints[breakpoint] ?? breakpoints.large;
  if (!activeLayout) {
    return;
  }
  const fieldTarget = field.target || '';
  const fieldPath = path ? `${path}/${fieldTarget}` : fieldTarget;
  const propertiesView = createPropertiesView(field, fieldPath, recursive, isMobile);
  return {
    id: field.id,
    fieldType: field.view.fieldType,
    properties: propertiesView,
    layout: activeLayout,
    layoutByBreakpoint: breakpoints,
    target: fieldTarget,
    path: fieldPath
  };
}
function createLayoutBreakpoints(layout, fieldId) {
  var _layout$large2, _layout$small2;
  const largeItem = (_layout$large2 = layout.large) == null || (_layout$large2 = _layout$large2.items) == null ? void 0 : _layout$large2.find(item => item.fieldId === fieldId);
  const largeLayoutView = largeItem && createLayoutView(largeItem);
  const smallItem = (_layout$small2 = layout.small) == null || (_layout$small2 = _layout$small2.items) == null ? void 0 : _layout$small2.find(item => item.fieldId === fieldId);
  const smallLayoutView = smallItem && createLayoutView(smallItem);
  return {
    large: largeLayoutView,
    small: smallLayoutView
  };
}
export function createPropertiesView(field, path, recursive, isMobile) {
  const {
    hidden,
    pii,
    nestedFormId,
    readOnly
  } = field;
  const {
    disabled,
    defaultValue,
    fieldType,
    form,
    ...rest
  } = field.view ?? {};
  const {
    required
  } = field.validation ?? {};
  const formView = form && recursive ? createFormView({
    form,
    path,
    recursive: false,
    isMobile,
    parentId: field.id
  }) : undefined;
  return {
    disabled: disabled ?? false,
    hidden: hidden ?? false,
    readOnly: readOnly ?? false,
    required: required ?? false,
    pii: pii ?? false,
    defaultValue: defaultValue ?? undefined,
    validation: field.validation ?? {},
    form: formView,
    ...(nestedFormId ? {
      nestedFormId
    } : {}),
    ...rest
  };
}
function createLayoutView(layout) {
  const {
    row,
    column,
    width,
    height
  } = layout;
  if (row === null || row === undefined || column === null || column === undefined || width === null || width === undefined || height === null || height === undefined) {
    return;
  }
  return {
    row,
    column,
    width,
    height,
    static: layout.static
  };
}
function createGridView(layout, isMobile) {
  const {
    columns,
    rowHeight,
    padding,
    margin
  } = (layout == null ? void 0 : layout.large) ?? {};
  let {
    horizontal: marginHorizontal
  } = margin ?? {};
  const {
    vertical: paddingVertical,
    horizontal: paddingHorizontal
  } = padding ?? {};
  const gridRowHeight = rowHeight ?? GRID_DEFAULTS.rowHeight;
  if (isMobile) {
    marginHorizontal = 0;
  }
  return {
    columns: columns ?? GRID_DEFAULTS.columns,
    rowHeight: gridRowHeight,
    margin: {
      vertical: (margin == null ? void 0 : margin.vertical) ?? GRID_DEFAULTS.margin.vertical,
      horizontal: marginHorizontal ?? GRID_DEFAULTS.margin.horizontal
    },
    padding: {
      vertical: paddingVertical ?? GRID_DEFAULTS.padding.vertical,
      horizontal: paddingHorizontal ?? GRID_DEFAULTS.padding.horizontal
    }
  };
}
function sortFieldViewsByRow(fieldA, fieldB) {
  return fieldA.layout.row - fieldB.layout.row;
}
function sortFieldViewsByColumn(fieldA, fieldB) {
  return fieldA.layout.column - fieldB.layout.column;
}
function createFieldViewV2(field, layout, path, recursive, isMobile, breakpoint) {
  var _field$inputOptions;
  if (breakpoint === void 0) {
    breakpoint = DEFAULT_BREAKPOINT;
  }
  if (!(field.id && field.identifier)) {
    return;
  }
  if (!layout) {
    return;
  }
  const breakpoints = createLayoutBreakpoints(layout, field.id);
  const activeLayout = breakpoints[breakpoint] ?? breakpoints.large;
  if (!activeLayout) {
    return;
  }
  const fieldTarget = ((_field$inputOptions = field.inputOptions) == null ? void 0 : _field$inputOptions.target) || '';
  const fieldPath = path ? `${path}/${fieldTarget}` : fieldTarget;
  const propertiesView = createPropertiesViewV2(field, fieldPath, recursive, isMobile);
  return {
    id: field.id,
    fieldType: field.identifier,
    properties: propertiesView,
    layout: activeLayout,
    layoutByBreakpoint: breakpoints,
    target: fieldTarget,
    path: fieldPath
  };
}
export function createPropertiesViewV2(field, path, recursive, isMobile, nestedForms) {
  var _field$displayOptions, _field$displayOptions2, _field$inputOptions2, _field$inputOptions3, _field$inputOptions4, _field$inputOptions5;
  const content = (_field$displayOptions = field.displayOptions) == null || (_field$displayOptions = _field$displayOptions.richContentOptions) == null ? void 0 : _field$displayOptions.richContent;
  const displayOptions = content ? {
    content
  } : undefined;
  const submitOptions = remapSubmitOptions((_field$displayOptions2 = field.displayOptions) == null ? void 0 : _field$displayOptions2.pageNavigationOptions);
  const options = getInputOptions(field) || displayOptions || submitOptions;
  const nestedForm = getNestedForm(nestedForms, (_field$inputOptions2 = field.inputOptions) == null ? void 0 : _field$inputOptions2.target);
  const formView = nestedForm && recursive ? createFormView({
    form: nestedForm,
    path,
    recursive: false,
    isMobile,
    useFieldsV2: true,
    parentId: field.id
  }) : undefined;
  return {
    disabled: false,
    defaultValue: undefined,
    hidden: field.hidden ?? false,
    readOnly: ((_field$inputOptions3 = field.inputOptions) == null ? void 0 : _field$inputOptions3.readOnly) ?? false,
    required: ((_field$inputOptions4 = field.inputOptions) == null ? void 0 : _field$inputOptions4.required) ?? false,
    pii: ((_field$inputOptions5 = field.inputOptions) == null ? void 0 : _field$inputOptions5.pii) ?? false,
    validation: toValidationV1(field) ?? {},
    form: formView,
    ...options
  };
}
function toValidationV1(field) {
  var _field$inputOptions6, _inputOptions$stringO, _inputOptions$numberO, _inputOptions$boolean, _inputOptions$arrayOp, _inputOptions$payment, _inputOptions$multili;
  const inputOptions = field.inputOptions;
  if (!inputOptions || !inputOptions.inputType) {
    return;
  }
  const required = (_field$inputOptions6 = field.inputOptions) == null ? void 0 : _field$inputOptions6.required;
  const validationByType = {
    [InputType.STRING]: {
      string: (_inputOptions$stringO = inputOptions.stringOptions) == null ? void 0 : _inputOptions$stringO.validation
    },
    [InputType.NUMBER]: {
      number: (_inputOptions$numberO = inputOptions.numberOptions) == null ? void 0 : _inputOptions$numberO.validation
    },
    [InputType.BOOLEAN]: {
      boolean: (_inputOptions$boolean = inputOptions.booleanOptions) == null ? void 0 : _inputOptions$boolean.validation
    },
    [InputType.ARRAY]: {
      array: toArrayValidationV1((_inputOptions$arrayOp = inputOptions.arrayOptions) == null ? void 0 : _inputOptions$arrayOp.validation)
    },
    [InputType.WIX_FILE]: {
      predefined: {
        format: ValidationFormat.WIX_FILE
      }
    },
    [InputType.PAYMENT]: {
      predefined: {
        paymentOptions: (_inputOptions$payment = inputOptions.paymentOptions) == null ? void 0 : _inputOptions$payment.validation,
        format: ValidationFormat.PAYMENT
      }
    },
    [InputType.MULTILINE_ADDRESS]: {
      predefined: {
        multilineAddressOptions: (_inputOptions$multili = inputOptions.multilineAddressOptions) == null ? void 0 : _inputOptions$multili.validation,
        format: ValidationFormat.MULTILINE_ADDRESS
      }
    },
    [InputType.SCHEDULING]: {
      predefined: {
        format: ValidationFormat.SCHEDULING
      }
    },
    [InputType.OBJECT]: undefined,
    [InputType.UNKNOWN_INPUT_TYPE]: undefined
  };
  const validationV1 = validationByType[inputOptions.inputType];
  if (!validationV1) {
    return;
  }
  return {
    required,
    ...validationV1
  };
}
function toArrayValidationV1(arrayValidation) {
  if (!arrayValidation) {
    return;
  }
  const {
    items,
    ...rest
  } = arrayValidation;
  if (!items) {
    return;
  }
  const arrayValidationV1 = {
    ...rest,
    items: {
      ...(items.stringOptions ? {
        string: items.stringOptions
      } : {}),
      ...(items.numberOptions ? {
        number: items.numberOptions
      } : {}),
      ...(items.booleanOptions ? {
        boolean: items.booleanOptions
      } : {}),
      ...(items.objectOptions ? {
        object: items.objectOptions
      } : {}),
      ...(items.integerOptions ? {
        integer: items.integerOptions
      } : {})
    }
  };
  return arrayValidationV1;
}
function getNestedForm(nestedForms, target) {
  var _ref3;
  return (_ref3 = nestedForms || []) == null || (_ref3 = _ref3.find(_ref4 => {
    let {
      targets
    } = _ref4;
    if (target && targets) {
      return targets.includes(target);
    }
    return false;
  })) == null ? void 0 : _ref3.form;
}
function getInputOptions(field) {
  var _field$inputOptions7;
  if (field.fieldType !== FieldType.INPUT) {
    return;
  }
  if (!field.inputOptions) {
    return;
  }
  const {
    stringOptions,
    numberOptions,
    booleanOptions,
    arrayOptions,
    wixFileOptions,
    multilineAddressOptions,
    paymentOptions,
    objectOptions
  } = field.inputOptions;
  const inputOptionsByType = {
    [InputType.STRING]: {
      componentType: stringOptions == null ? void 0 : stringOptions.componentType,
      componentOptionsByType: {
        [StringComponentType.TEXT_INPUT]: remapShowToHide(stringOptions == null ? void 0 : stringOptions.textInputOptions),
        [StringComponentType.DROPDOWN]: remapShowToHide(remapDefaultOption(stringOptions == null ? void 0 : stringOptions.dropdownOptions)),
        [StringComponentType.DATE_INPUT]: remapShowToHide(stringOptions == null ? void 0 : stringOptions.dateInputOptions),
        [StringComponentType.DATE_PICKER]: remapShowToHide(stringOptions == null ? void 0 : stringOptions.datePickerOptions),
        [StringComponentType.DATE_TIME]: remapShowToHide(stringOptions == null ? void 0 : stringOptions.dateTimeOptions),
        [StringComponentType.TIME_INPUT]: remapShowToHide(stringOptions == null ? void 0 : stringOptions.timeInputOptions),
        [StringComponentType.PHONE_INPUT]: remapShowToHide(remapShowCountryFlag(stringOptions == null ? void 0 : stringOptions.phoneInputOptions)),
        [StringComponentType.RADIO_GROUP]: remapShowToHide(remapDefaultOption(remapAddOther(remapNumberOfColumns(stringOptions == null ? void 0 : stringOptions.radioGroupOptions)))),
        [StringComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    [InputType.NUMBER]: {
      componentType: numberOptions == null ? void 0 : numberOptions.componentType,
      componentOptionsByType: {
        [NumberComponentType.NUMBER_INPUT]: remapShowToHide(numberOptions == null ? void 0 : numberOptions.numberInputOptions),
        [NumberComponentType.RATING_INPUT]: remapShowToHide(numberOptions == null ? void 0 : numberOptions.ratingInputOptions),
        [NumberComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    [InputType.BOOLEAN]: {
      componentType: booleanOptions == null ? void 0 : booleanOptions.componentType,
      componentOptionsByType: {
        [BooleanComponentType.CHECKBOX]: remapChecked(booleanOptions == null ? void 0 : booleanOptions.checkboxOptions),
        [BooleanComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    [InputType.ARRAY]: {
      componentType: arrayOptions == null ? void 0 : arrayOptions.componentType,
      componentOptionsByType: {
        [ArrayComponentType.CHECKBOX_GROUP]: remapShowToHide(remapDefaultOptions(remapAddOther(remapNumberOfColumns(arrayOptions == null ? void 0 : arrayOptions.checkboxGroupOptions)))),
        [ArrayComponentType.TAGS]: remapShowToHide(remapDefaultOptions(remapAddOther(remapNumberOfColumns(arrayOptions == null ? void 0 : arrayOptions.tagsOptions)))),
        [ArrayComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    [InputType.PAYMENT]: {
      componentType: paymentOptions == null ? void 0 : paymentOptions.componentType,
      componentOptionsByType: {
        [PaymentComponentType.CHECKBOX_GROUP]: remapShowToHide(remapImageFit(remapDefaultPayments(paymentOptions == null ? void 0 : paymentOptions.checkboxGroupOptions))),
        [PaymentComponentType.PAYMENT_INPUT]: remapShowToHide(paymentOptions == null ? void 0 : paymentOptions.paymentInputOptions),
        [PaymentComponentType.FIXED_PAYMENT]: remapShowToHide(paymentOptions == null ? void 0 : paymentOptions.fixedPaymentOptions),
        [PaymentComponentType.DONATION_INPUT]: remapShowToHide(remapDefaultPayment(remapAddOther(remapNumberOfColumns(paymentOptions == null ? void 0 : paymentOptions.donationInputOptions)))),
        [PaymentComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    [InputType.WIX_FILE]: {
      componentType: wixFileOptions == null ? void 0 : wixFileOptions.componentType,
      componentOptionsByType: {
        [WixFileComponentType.FILE_UPLOAD]: forceFileUploadPlaceholder(remapShowToHide(remapUploadFileFormats(remapFileLimit(wixFileOptions == null ? void 0 : wixFileOptions.fileUploadOptions)))),
        [WixFileComponentType.SIGNATURE]: remapShowToHide(remapImageUploadEnabled(wixFileOptions == null ? void 0 : wixFileOptions.signatureOptions)),
        [WixFileComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    [InputType.MULTILINE_ADDRESS]: {
      componentType: multilineAddressOptions == null ? void 0 : multilineAddressOptions.componentType,
      componentOptionsByType: {
        [MultilineAddressComponentType.MULTILINE_ADDRESS]: forceHideLabel(multilineAddressOptions == null ? void 0 : multilineAddressOptions.multilineAddressOptions),
        [MultilineAddressComponentType.UNKNOWN_COMPONENT_TYPE]: undefined
      }
    },
    // TODO: implement
    [InputType.SCHEDULING]: {
      componentType: undefined,
      componentOptionsByType: {}
    },
    [InputType.OBJECT]: {
      componentType: undefined,
      componentOptionsByType: {}
    },
    [InputType.UNKNOWN_INPUT_TYPE]: {
      componentType: undefined,
      componentOptionsByType: {}
    }
  };
  const inputType = (_field$inputOptions7 = field.inputOptions) == null ? void 0 : _field$inputOptions7.inputType;
  if (!inputType) {
    return;
  }
  if (inputType === InputType.OBJECT) {
    return objectOptions;
  }
  const {
    componentType,
    componentOptionsByType
  } = inputOptionsByType[inputType];
  if (componentType && hasKey(componentOptionsByType, componentType)) {
    return componentOptionsByType[componentType];
  }
  return;
}
function remapShowToHide(options) {
  if (!options) {
    return undefined;
  }
  const {
    showLabel,
    showPlaceholder,
    ...rest
  } = options;
  return {
    ...rest,
    ...(typeof showLabel === 'boolean' ? {
      hideLabel: !showLabel
    } : {
      hideLabel: false
    }),
    ...(typeof showPlaceholder === 'boolean' ? {
      hidePlaceholder: !showPlaceholder
    } : {})
  };
}
function remapShowCountryFlag(options) {
  if (!options) {
    return undefined;
  }
  const {
    showCountryFlag,
    ...rest
  } = options;
  return {
    ...rest,
    showFlag: showCountryFlag
  };
}
function remapNumberOfColumns(options) {
  if (!options) {
    return undefined;
  }
  const {
    numberOfColumns,
    ...rest
  } = options;
  if (typeof numberOfColumns === 'undefined') {
    return options;
  }
  const columnMap = {
    [NumberOfColumns.ONE]: 1,
    [NumberOfColumns.TWO]: 2,
    [NumberOfColumns.THREE]: 3,
    [NumberOfColumns.ZERO]: undefined,
    [NumberOfColumns.UNKNOWN]: undefined
  };
  const columns = columnMap[numberOfColumns];
  if (typeof columns === 'undefined') {
    return options;
  }
  return {
    ...rest,
    numberOfColumns: columns
  };
}
function remapImageUploadEnabled(options) {
  if (!options) {
    return undefined;
  }
  const {
    imageUploadEnabled,
    ...rest
  } = options;
  return {
    ...rest,
    uploadEnabled: imageUploadEnabled
  };
}
function remapAddOther(options) {
  if (!options) {
    return undefined;
  }
  const {
    customOption,
    ...rest
  } = options;
  if (!customOption) {
    return options;
  }
  return {
    ...rest,
    addOther: true,
    ...(customOption.label ? {
      addOtherLabel: customOption.label
    } : {}),
    ...(customOption.placeholder ? {
      addOtherPlaceholder: customOption.placeholder
    } : {})
  };
}
function remapFileLimit(options) {
  if (!options) {
    return undefined;
  }
  const {
    fileLimit,
    ...rest
  } = options;
  if (typeof fileLimit === 'undefined') {
    return options;
  }
  return {
    ...rest,
    valueSliderLimit: fileLimit
  };
}
function remapUploadFileFormats(options) {
  if (!options) {
    return;
  }
  const {
    uploadFileFormats,
    ...rest
  } = options;
  const formatMap = {
    [UploadFileFormat.ARCHIVE]: 'Archive',
    [UploadFileFormat.VIDEO]: 'Video',
    [UploadFileFormat.DOCUMENT]: 'Document',
    [UploadFileFormat.IMAGE]: 'Image',
    [UploadFileFormat.AUDIO]: 'Audio'
  };
  if (!uploadFileFormats) {
    return options;
  }
  const formats = uploadFileFormats.reduce((result, format) => {
    const oldFormat = formatMap[format];
    if (oldFormat) {
      result.push(oldFormat);
    }
    return result;
  }, []);
  return {
    ...rest,
    uploadFileFormats: formats
  };
}
function remapChecked(options) {
  if (!options) {
    return undefined;
  }
  const {
    checked,
    ...rest
  } = options;
  if (typeof checked === 'undefined') {
    return options;
  }
  return {
    ...rest,
    defaultValue: checked
  };
}
function forceHideLabel(options) {
  if (!options) {
    return undefined;
  }
  return {
    ...options,
    hideLabel: true
  };
}
function forceFileUploadPlaceholder(options) {
  if (!options) {
    return undefined;
  }
  return {
    ...options,
    placeholder: 'File upload'
  };
}
function remapDefaultOption(_options) {
  if (!_options) {
    return undefined;
  }
  const {
    options,
    ...rest
  } = _options;
  if (!options) {
    return _options;
  }
  const defaultOption = options.find(option => option.default);
  return {
    ...rest,
    options: options.map(_ref5 => {
      let {
        default: _,
        ...option
      } = _ref5;
      return option;
    }),
    ...(defaultOption ? {
      defaultValue: defaultOption.value ?? defaultOption.label
    } : {})
  };
}
function remapDefaultOptions(_options) {
  if (!_options) {
    return undefined;
  }
  const {
    options,
    ...rest
  } = _options;
  if (!options) {
    return _options;
  }
  const defaultOptions = options.filter(option => option.default);
  return {
    ...rest,
    options: options.map(_ref6 => {
      let {
        default: _,
        ...option
      } = _ref6;
      return option;
    }),
    ...(defaultOptions.length > 0 ? {
      defaultValue: defaultOptions.map(option => option.value ?? option.label)
    } : {})
  };
}
function remapDefaultPayment(_options) {
  if (!_options) {
    return undefined;
  }
  const {
    options,
    ...rest
  } = _options;
  if (!options) {
    return _options;
  }
  const defaultOption = options.find(option => option.default);
  return {
    ...rest,
    options: options.map(_ref7 => {
      let {
        default: _,
        ...option
      } = _ref7;
      return option;
    }),
    ...(defaultOption ? {
      defaultValue: defaultOption
    } : {})
  };
}
function remapDefaultPayments(_options) {
  if (!_options) {
    return undefined;
  }
  const {
    options,
    ...rest
  } = _options;
  if (!options) {
    return _options;
  }
  const defaultOptions = options.filter(option => option.default);
  return {
    ...rest,
    options: options.map(_ref8 => {
      let {
        default: _,
        ...option
      } = _ref8;
      return option;
    }),
    ...(defaultOptions.length > 0 ? {
      defaultValue: defaultOptions
    } : {})
  };
}
function remapImageFit(options) {
  if (!options) {
    return undefined;
  }
  const {
    imageFit,
    ...rest
  } = options;
  if (typeof imageFit === 'undefined') {
    return options;
  }
  const imageResizeMap = {
    [ImageFit.CONTAIN]: 'FIT',
    [ImageFit.COVER]: 'CROP',
    [ImageFit.UNKNOWN_IMAGE_FIT]: 'FIT'
  };
  const imageResize = imageResizeMap[imageFit];
  return {
    ...rest,
    imageResize
  };
}
function remapSubmitOptions(pageNavigationOptions) {
  if (!pageNavigationOptions) {
    return undefined;
  }
  const {
    submitText,
    nextPageText,
    previousPageText
  } = pageNavigationOptions;
  return {
    submitText,
    nextText: nextPageText,
    previousText: previousPageText
  };
}
function hasKey(obj, key) {
  return key in obj;
}
//# sourceMappingURL=form-view.js.map